(() => {
  if (customElements.get('arista-watch-shop-stream')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class AristaWatchShopStream extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      document.body.classList.add('arista-watch-page-active');
      this.saveData = Boolean(navigator.connection?.saveData);
      this.cards = [...this.querySelectorAll('[data-arista-watch-card]')];
      this.filters = [...this.querySelectorAll('[data-arista-watch-filter]')];
      this.empty = this.querySelector('[data-arista-watch-empty]');
      this.modal = this.querySelector('[data-arista-watch-modal]');
      this.modalDialog = this.modal?.querySelector('[role="dialog"]');
      this.modalImage = this.modal?.querySelector('[data-arista-watch-modal-image]');
      this.modalVideo = this.modal?.querySelector('[data-arista-watch-modal-video]');
      this.modalTitle = this.modal?.querySelector('[data-arista-watch-modal-title]');
      this.modalShop = this.modal?.querySelector('[data-arista-watch-modal-shop]');
      this.items = this.cards
        .map((card) => ({
          card,
          video: card.querySelector('[data-arista-watch-video]'),
          source: card.querySelector('[data-arista-watch-video] source[data-src]'),
          button: card.querySelector('[data-arista-watch-toggle]'),
          label: card.querySelector('[data-arista-watch-label]'),
          status: card.querySelector('[data-arista-watch-status]'),
          title: card.querySelector('.arista-watch-page__card-title')?.textContent.trim() || 'Arista story',
          userPaused: false,
        }))
        .filter((item) => item.video && item.button);

      this.onFilterClick = (event) => {
        const button = event.target.closest('[data-arista-watch-filter]');
        if (button) this.applyFilter(button);
      };
      this.onShareClick = (event) => {
        const button = event.target.closest('[data-arista-watch-share]');
        if (button) this.shareStory(button);
      };
      this.onExpandClick = (event) => {
        const button = event.target.closest('[data-arista-watch-expand]');
        if (button) this.openStoryModal(button);
      };
      this.onModalClick = (event) => {
        if (event.target.closest('[data-arista-watch-modal-close]')) this.closeStoryModal();
      };
      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.modal && !this.modal.hidden) this.closeStoryModal();
      };
      this.onVisibilityChange = () => {
        if (document.hidden) this.pauseAll();
      };

      this.addEventListener('click', this.onFilterClick);
      this.addEventListener('click', this.onShareClick);
      this.addEventListener('click', this.onExpandClick);
      this.modal?.addEventListener('click', this.onModalClick);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.items.forEach((item) => {
        item.video.muted = true;
        item.onToggle = () => this.toggle(item);
        item.onReady = () => item.video.classList.add('is-ready');
        item.onPlay = () => {
          this.pauseOthers(item.video);
          this.updateState(item, false);
        };
        item.onPause = () => this.updateState(item, item.userPaused);
        item.button.addEventListener('click', item.onToggle);
        item.video.addEventListener('loadeddata', item.onReady);
        item.video.addEventListener('play', item.onPlay);
        item.video.addEventListener('pause', item.onPause);
        this.updateState(item, false);
      });

      this.setupSourceObserver();
      this.setupPlaybackObserver();
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.sourceObserver?.disconnect();
      this.playbackObserver?.disconnect();
      this.removeEventListener('click', this.onFilterClick);
      this.removeEventListener('click', this.onShareClick);
      this.removeEventListener('click', this.onExpandClick);
      this.modal?.removeEventListener('click', this.onModalClick);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.items.forEach((item) => {
        item.video.pause();
        item.button.removeEventListener('click', item.onToggle);
        item.video.removeEventListener('loadeddata', item.onReady);
        item.video.removeEventListener('play', item.onPlay);
        item.video.removeEventListener('pause', item.onPause);
      });
      document.body.classList.remove('arista-watch-page-active');
      if (this.modal && !this.modal.hidden) this.closeStoryModal({ restoreFocus: false });
      this.initialized = false;
    }

    setupSourceObserver() {
      if (reducedMotion.matches || this.saveData) return;
      if (!('IntersectionObserver' in window)) return;

      this.sourceObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const item = this.items.find((candidate) => candidate.card === entry.target);
            if (item) this.ensureSource(item);
            this.sourceObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '50% 0px' }
      );

      this.items.forEach((item) => this.sourceObserver.observe(item.card));
    }

    setupPlaybackObserver() {
      if (reducedMotion.matches || this.saveData || !('IntersectionObserver' in window)) return;

      this.visibility = new Map();
      this.playbackObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const item = this.items.find((candidate) => candidate.card === entry.target);
            if (!item) return;
            this.visibility.set(item, entry.isIntersecting ? entry.intersectionRatio : 0);
          });

          const active = this.items
            .filter((item) => !item.card.hidden && !item.userPaused && (this.visibility.get(item) || 0) >= 0.62)
            .sort((first, second) => (this.visibility.get(second) || 0) - (this.visibility.get(first) || 0))[0];

          this.items.forEach((item) => {
            if (item === active) {
              if (item.video.paused) this.play(item, false);
            } else if (!item.video.paused) {
              item.video.pause();
            }
          });
        },
        { threshold: [0, 0.01, 0.62, 1] }
      );

      this.items.forEach((item) => this.playbackObserver.observe(item.card));
    }

    applyFilter(button) {
      const filter = button.dataset.aristaWatchFilter || 'all';
      let visible = 0;

      this.filters.forEach((candidate) => {
        const active = candidate === button;
        candidate.classList.toggle('is-active', active);
        candidate.setAttribute('aria-pressed', String(active));
      });

      this.cards.forEach((card) => {
        const matches = filter === 'all' || card.dataset.aristaWatchCategory === filter;
        card.hidden = !matches;
        if (matches) visible += 1;

        const item = this.items.find((candidate) => candidate.card === card);
        if (!matches && item && !item.video.paused) item.video.pause();
      });

      if (this.empty) this.empty.hidden = visible > 0;
    }

    toggle(item) {
      if (item.video.paused) {
        item.userPaused = false;
        this.play(item, true);
      } else {
        item.userPaused = true;
        item.video.pause();
      }
    }

    play(item, announce) {
      this.ensureSource(item);
      this.pauseOthers(item.video);
      const result = item.video.play();
      if (!result) return;

      result
        .then(() => this.updateState(item, announce))
        .catch(() => this.updateState(item, false, `${item.title} could not start.`));
    }

    ensureSource(item) {
      if (!item.source?.dataset.src || item.source.src) return;
      item.source.src = item.source.dataset.src;
      item.source.removeAttribute('data-src');
      item.video.load();
    }

    pauseOthers(activeVideo) {
      this.items.forEach((item) => {
        if (item.video !== activeVideo && !item.video.paused) item.video.pause();
      });
    }

    pauseAll() {
      this.items.forEach((item) => item.video.pause());
    }

    updateState(item, announce, message) {
      const playing = !item.video.paused && !item.video.ended;
      if (item.label) item.label.textContent = playing ? 'Pause' : 'Play';
      item.button.dataset.playing = String(playing);
      item.button.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} ${item.title}`);
      if (item.status) {
        const statusText = message || `${item.title} is ${playing ? 'playing' : 'paused'}.`;
        item.status.setAttribute('aria-live', announce || message ? 'polite' : 'off');

        if (announce || message) {
          item.status.textContent = '';
          window.requestAnimationFrame(() => {
            item.status.textContent = statusText;
          });
        } else {
          item.status.textContent = statusText;
        }
      }
    }

    openStoryModal(button) {
      if (!this.modal || !this.modalDialog) return;

      this.pauseAll();
      this.lastModalTrigger = button;
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      const title = button.dataset.storyTitle || 'Arista story';
      const imageSource = button.dataset.imageSrc || '';
      const videoSource = button.dataset.videoSrc || '';
      const destination = button.dataset.destination || '';

      if (this.modalTitle) this.modalTitle.textContent = title;
      if (this.modalShop) {
        this.modalShop.href = destination;
        this.modalShop.hidden = !destination;
      }

      if (this.modalImage) {
        this.modalImage.src = imageSource;
        this.modalImage.alt = title;
        this.modalImage.hidden = Boolean(videoSource) || !imageSource;
      }

      if (this.modalVideo) {
        this.modalVideo.pause();
        this.modalVideo.src = videoSource;
        this.modalVideo.poster = imageSource;
        this.modalVideo.hidden = !videoSource;
        if (videoSource) this.modalVideo.load();
      }

      this.modal.hidden = false;
      window.requestAnimationFrame(() => this.modalDialog.focus());
    }

    closeStoryModal({ restoreFocus = true } = {}) {
      if (!this.modal || this.modal.hidden) return;
      if (this.modalVideo) {
        this.modalVideo.pause();
        this.modalVideo.removeAttribute('src');
        this.modalVideo.load();
      }
      this.modal.hidden = true;
      document.body.style.overflow = this.previousBodyOverflow || '';
      if (restoreFocus) this.lastModalTrigger?.focus();
    }

    async shareStory(button) {
      const url = button.dataset.shareUrl || window.location.href;
      const title = button.dataset.shareTitle || document.title;
      const text = button.dataset.shareText || '';
      const status = button.closest('.arista-watch-page__copy')?.querySelector('[data-arista-share-status]');

      try {
        if (navigator.share) {
          await navigator.share({ title, text, url });
          if (status) status.textContent = 'Share menu opened.';
          return;
        }

        await navigator.clipboard.writeText(url);
        if (status) status.textContent = this.dataset.saveLabel || 'Link copied.';
      } catch (error) {
        if (error?.name === 'AbortError') return;
        if (status) status.textContent = 'Copy this page link from the browser to share it.';
      }
    }
  }

  customElements.define('arista-watch-shop-stream', AristaWatchShopStream);
})();
